import searchAtomPair from 'atoms/search-atom';
import {
  Button,
  Checkbox,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { EmptyState } from 'components/empty-state';
import { PlainLink } from 'components/link';
import { SkeletonTableRows } from 'components/skeleton-table-rows';
import { PaginatedTable } from 'components/table/paginated-table';
import EmptySearchSvg from 'images/srp-illustration-empty-search.svg';
import { useAtom, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';
import { entityLinkingMutationAtom } from 'pages/crm-tracts-subtracts/atoms';
import { useEffect, useMemo, useState } from 'react';
import {
  EntityLinkResponse,
  SalesforceAccountResponse,
} from 'types/api-responses';
import style from './link-crm-account-panel.module.scss';

interface LinkCrmAccountPanelProps {
  isOpen: boolean;
  selectedEntity: number | null;
  initialSearchText?: string;
  onClose: () => void;
  onLinkSuccess: (
    entityId: number,
    accountId: string,
    response: EntityLinkResponse
  ) => void;
  onLinkError: (error: unknown) => void;
  onLinkStart: (accountId: string) => void;
}

export const LinkCrmAccountPanel = ({
  isOpen,
  selectedEntity,
  initialSearchText = '',
  onClose,
  onLinkSuccess,
  onLinkError,
  onLinkStart,
}: LinkCrmAccountPanelProps) => {
  const [linkPanelSearchText, setLinkPanelSearchText] = useState('');
  const [selectedLinkAccount, setSelectedLinkAccount] = useState<string | null>(
    null
  );
  const [linkPanelPage, setLinkPanelPage] = useState(1);
  const [linkPanelPageSize, setLinkPanelPageSize] = useState(16);

  // Salesforce accounts search atom
  const { searchAtom: salesforceAccountsSearchAtom } = useMemo(
    () => searchAtomPair<SalesforceAccountResponse>('salesforceAccounts'),
    []
  );
  const [salesforceAccountsSearch, setSalesforceAccountsSearch] = useAtom(
    salesforceAccountsSearchAtom
  );

  const entityLinkingMutation = useSetAtom(entityLinkingMutationAtom);

  const debouncedCrmAccountsSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value.trim().length > 0) {
          setSalesforceAccountsSearch((current) => ({
            text: value,
            page: 1,
            pageSize: current?.pageSize || 16,
          }));
        } else {
          setSalesforceAccountsSearch(undefined);
        }
      }, 400),
    [setSalesforceAccountsSearch]
  );

  // Initialize search if initialSearchText is provided (only on panel open)
  useEffect(() => {
    if (isOpen && initialSearchText.trim().length > 0) {
      setLinkPanelSearchText(initialSearchText);
      debouncedCrmAccountsSearch(initialSearchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialSearchText]);

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setLinkPanelSearchText('');
      setSelectedLinkAccount(null);
      setLinkPanelPage(1);
      setLinkPanelPageSize(16);
    }
  }, [isOpen]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedCrmAccountsSearch.cancel();
    };
  }, [debouncedCrmAccountsSearch]);

  const handleSave = () => {
    if (selectedLinkAccount && selectedEntity) {
      onLinkStart(selectedLinkAccount);
      entityLinkingMutation({
        url: `/entities/${selectedEntity}/entity_links`,
        type: 'POST',
        data: {
          accountId: selectedLinkAccount,
        },
        onSuccess: (data: EntityLinkResponse) => {
          onLinkSuccess(selectedEntity, selectedLinkAccount, data);
        },
        onError: onLinkError,
      });
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={style.linkPanel}>
      <div className={style.linkPanelHeader}>
        <h3 className={style.linkPanelTitle}>Link CRM Account</h3>
        <div className={style.linkPanelActions}>
          <Button kind="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            kind="primary"
            size="sm"
            disabled={!selectedLinkAccount}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </div>
      <div className={style.linkPanelSearch}>
        <Search
          labelText="Search"
          placeholder="Search CRM accounts"
          size="lg"
          value={linkPanelSearchText}
          onChange={(e) => {
            if (e.target.value.trim().length === 0) {
              setSalesforceAccountsSearch(undefined);
            }
            setLinkPanelSearchText(e.target.value);
            setSelectedLinkAccount(null);
            debouncedCrmAccountsSearch(e.target.value);
          }}
        />
      </div>
      <div className={style.linkPanelContent}>
        <h4 className={style.linkPanelSectionTitle}>Search Results</h4>
        {linkPanelSearchText.trim().length === 0 ? (
          <EmptyState
            icon={EmptySearchSvg}
            headerText="Please search for CRM accounts"
            helperText="Enter a search term in the search bar above to find CRM accounts"
            className={style.linkPanelEmptyState}
          />
        ) : (
          <PaginatedTable
            paginationOnTop={false}
            persistentPagination={false}
            pageSize={linkPanelPageSize}
            pageSizes={[16, 24, 48, 64]}
            page={linkPanelPage}
            totalItems={salesforceAccountsSearch.data?.totalCount || 0}
            isLoading={salesforceAccountsSearch.loading || false}
            loadingComponent={
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader></TableHeader>
                    <TableHeader>CRM Account</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <SkeletonTableRows rows={linkPanelPageSize} columns={2} />
                </TableBody>
              </Table>
            }
            onPaginate={({ page, pageSize }) => {
              setLinkPanelPage(page);
              setLinkPanelPageSize(pageSize);
              setSalesforceAccountsSearch(() => ({
                text: linkPanelSearchText,
                page,
                pageSize,
              }));
            }}
            emptyStateComponent={
              <EmptyState
                icon={EmptySearchSvg}
                headerText="No CRM Accounts Found"
                helperText="Try adjusting your search criteria"
                className={style.linkPanelEmptyState}
              />
            }
          >
            <TableHead>
              <TableRow>
                <TableHeader></TableHeader>
                <TableHeader>CRM Account</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {salesforceAccountsSearch.data?.results &&
                salesforceAccountsSearch.data.results.map(
                  (account: SalesforceAccountResponse) => (
                    <TableRow key={account.salesforceId}>
                      <TableCell>
                        <Checkbox
                          id={`link-account-${account.salesforceId}`}
                          labelText=""
                          checked={selectedLinkAccount === account.salesforceId}
                          onChange={(
                            _evt,
                            { checked }: { checked: boolean }
                          ) => {
                            if (checked) {
                              setSelectedLinkAccount(account.salesforceId);
                            } else if (
                              selectedLinkAccount === account.salesforceId
                            ) {
                              setSelectedLinkAccount(null);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <div className={style.accountInfo}>
                          <div className={style.accountName}>
                            <PlainLink
                              href={account.salesforceHref}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {account.name}
                            </PlainLink>
                          </div>
                          <div className={style.accountType}>
                            <span className={style.label}>Type:</span>
                            <span className={style.value}> {account.type}</span>
                          </div>
                          <div className={style.accountAddress}>
                            <span className={style.label}>Address:</span>
                            <span className={style.value}>
                              {account.billingAddress.street &&
                              account.billingAddress.city
                                ? `${account.billingAddress.street}, ${account.billingAddress.city}, ${account.billingAddress.state} ${account.billingAddress.postalCode}`
                                : account.billingAddress.street || '--'}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                )}
            </TableBody>
          </PaginatedTable>
        )}
      </div>
    </div>
  );
};
